/**
 * THE SWEEP-ONCE HALF OF `installSeoHead` — the fix for DEFECT F-A
 * (Q-453 = A, decided 2026-08-11; C-603).
 *
 * THE DEFECT, IN ONE SENTENCE. Unhead removes only the side effects it OWNS.
 * The baked managed tags were written into the served HTML by the build-time
 * emitter before any JavaScript ran, so Unhead never owned them and never
 * removes them — which means a managed tag present in the BAKED head and ABSENT
 * from the runtime entry is left stranded in the DOM for the rest of the
 * session, while every tag the new route DOES declare moves correctly.
 * Measured live across the fleet: a stale `meta keywords` on five sites and a
 * stale `article:published_time` on iron-oak
 * (`.docs/analysis/installseohead-defect-fa-explainer-2026-08-11.md` §2).
 *
 * THE FIX. On the FIRST matched in-app navigation, remove the baked managed
 * tags that the incoming head input does not re-declare. From that moment the
 * runtime is the sole owner of the managed head and Unhead's own reconciliation
 * (proven correct by the C-362 fixture, on both shipped versions) handles every
 * subsequent navigation. One sweep is therefore not a budget — it is
 * SUFFICIENT, and §"WHY ONCE IS ENOUGH" below is the argument.
 *
 * ── THE FOUR REFUSALS ──────────────────────────────────────────────────────
 *
 *   S1  NEVER SWEEP JSON-LD. This is the failure mode Q-453 named explicitly
 *       when it chose option A. `<script type="application/ld+json">` IS in the
 *       emitter's managed set, but the runtime DELIBERATELY never re-asserts it
 *       (the baked graph is built from inputs that do not exist at runtime:
 *       content.yaml reviews, business licence, FAQ pairs, breadcrumb trails).
 *       So a sweep that treated "managed" as "sweepable" would DELETE the graph
 *       outright and leave the page with none — strictly worse than the stale
 *       graph it replaced. Scripts are never snapshotted, so they are
 *       unreachable by this module rather than merely skipped.
 *
 *   S2  NEVER SWEEP A TAG THE RUNTIME IS ABOUT TO DECLARE. The sweep set is the
 *       baked snapshot MINUS the identities in the incoming input. This is not
 *       an optimisation — it is what makes the sweep SAFE. Unhead reconciles
 *       against (and may adopt) a baked element whose identity appears in an
 *       entry; ripping such an element out from under Unhead would desync its
 *       side-effect bookkeeping and could leave the tag permanently missing.
 *       Excluding them means this module only ever touches elements Unhead has
 *       no entry for and therefore can never have adopted. It also means the
 *       swept set IS exactly the strandable set, by construction.
 *
 *   S3  NEVER SWEEP `<title>`. Title is emitted unconditionally by
 *       `buildHeadTags`, so it is always re-asserted and can never strand; and
 *       it is the one managed tag with singleton semantics in the DOM and in
 *       `document.title`. It is excluded from the snapshot for the same reason
 *       as S1 — unreachable beats skipped.
 *
 *   S4  THE SNAPSHOT IS TAKEN AT INSTALL TIME, NOT AT SWEEP TIME. "Baked" means
 *       "present in the document the browser fetched". `installSeoHead` runs at
 *       app entry, after `createHead()` but BEFORE `app.mount()` — and Unhead's
 *       DOM write is debounced to a later task — so `document.head` at that
 *       instant holds the served tags and nothing else. Snapshotting lazily on
 *       the first navigation would instead capture Unhead's own output and let
 *       the sweep delete live runtime tags. The install-time snapshot is what
 *       makes "the ones the static emitter baked" a fact rather than a guess.
 *
 * ── WHY ONCE IS ENOUGH ─────────────────────────────────────────────────────
 * After the sweep, every managed element in the head is either (a) one the
 * runtime just declared — Unhead owns it, and drops it on a later navigation
 * that omits it (the RUNTIME->RUNTIME half, already proven working by ITEM 2b),
 * or (b) gone. There is no third category left for a later navigation to
 * strand, so a second sweep would have an empty set by definition. Sweeping
 * repeatedly would also mean holding DOM references for the life of the
 * session; the snapshot is released as soon as it is spent.
 *
 * ── WHAT CRAWLERS SEE: NOTHING CHANGES ─────────────────────────────────────
 * The sweep can only fire on a MATCHED IN-APP NAVIGATION. `installSeoHead`'s R5
 * already guarantees the fetched document is never written to, and a crawler or
 * social scraper fetches each URL as an isolated page load and never performs an
 * in-app navigation. The prerender path is likewise unaffected: the Playwright
 * renderer loads each route as a fresh document, so no in-app navigation ever
 * occurs and no sweep is reachable. Baked bytes are identical before and after
 * this change — which is also why it cannot disturb the emitter-output pins in
 * `runtimeVsBakedJsonLd.test.ts` (C-418): this module changes neither emitter.
 */

import { isManagedMetaTag, isManagedLinkRel } from './spliceHeadHtml'
import type { HeadMetaTag, HeadLinkTag } from './headTags'

/**
 * A stable identity for a managed head tag, shared by the DOM side and the
 * head-input side so the two can be compared as sets.
 *
 * The shapes mirror how the head libraries dedupe:
 *   - `meta|name|<name>`             — `description`, `keywords`, `twitter:*`
 *   - `meta|property|<property>`     — `og:*`, `article:*`
 *   - `link|canonical`               — the singleton canonical
 *   - `link|alternate|<hreflang>`    — one per hreflang; the hreflang is part of
 *     the identity because a page may legitimately declare several alternates
 *     and they are only interchangeable within a single language.
 */
type ManagedTagIdentity = string

function metaIdentity(
  name: string | null | undefined,
  property: string | null | undefined
): ManagedTagIdentity | null {
  if (typeof property === 'string' && property !== '') {
    return `meta|property|${property.toLowerCase()}`
  }
  if (typeof name === 'string' && name !== '') {
    return `meta|name|${name.toLowerCase()}`
  }
  return null
}

function linkIdentity(
  rel: string | null | undefined,
  hreflang: string | null | undefined
): ManagedTagIdentity | null {
  if (typeof rel !== 'string' || rel === '') return null
  const r = rel.toLowerCase()
  if (r === 'alternate') {
    return `link|alternate|${typeof hreflang === 'string' ? hreflang.toLowerCase() : ''}`
  }
  return `link|${r}`
}

/**
 * The identities the runtime is ABOUT to assert — the S2 exclusion set.
 *
 * Deliberately typed against {@link HeadMetaTag}/{@link HeadLinkTag}, the exact
 * structures `buildHeadTags` returns, so a future field on either flows through
 * the same identity function the DOM side uses.
 */
export function headInputIdentities(input: {
  meta?: readonly HeadMetaTag[]
  link?: readonly HeadLinkTag[]
}): Set<ManagedTagIdentity> {
  const ids = new Set<ManagedTagIdentity>()

  for (const m of input.meta ?? []) {
    if (!m) continue
    const id = metaIdentity(m.name, m.property)
    if (id) ids.add(id)
  }
  for (const l of input.link ?? []) {
    if (!l) continue
    const id = linkIdentity(l.rel, l.hreflang)
    if (id) ids.add(id)
  }

  return ids
}

/**
 * Capture the managed tags the BUILD baked into the served document (S4).
 *
 * Call this at INSTALL time. `<title>` (S3) and `ld+json` scripts (S1) are not
 * collected at all, so no later mistake can sweep them.
 *
 * Returns a plain array of live element references. It is empty — and the whole
 * feature inert — when there is no document (SSR / node build context), which is
 * the same defensive posture `readInjectedPages` takes.
 */
export function snapshotBakedManagedHeadTags(doc?: Document): Element[] {
  const d = doc ?? (typeof document !== 'undefined' ? document : undefined)
  if (!d || !d.head) return []

  const out: Element[] = []

  // S1/S3 by construction: only `meta` and `link` are ever considered.
  for (const el of Array.from(d.head.querySelectorAll('meta'))) {
    if (isManagedMetaTag(el.getAttribute('name'), el.getAttribute('property'))) {
      out.push(el)
    }
  }
  for (const el of Array.from(d.head.querySelectorAll('link'))) {
    if (isManagedLinkRel(el.getAttribute('rel'))) {
      out.push(el)
    }
  }

  return out
}

/**
 * Remove the baked managed tags the incoming head input does NOT re-declare.
 *
 * @param snapshot the install-time snapshot from
 *   {@link snapshotBakedManagedHeadTags}
 * @param input the head payload about to be pushed to the head client — the S2
 *   exclusion set is derived from it
 * @returns how many elements were actually removed, so the caller can REPORT
 *   the sweep instead of performing it silently
 *
 * Elements that have already left the document are skipped rather than removed
 * again: a baked element can legitimately disappear between install and the
 * first navigation (a view's own `useSEO` may have caused Unhead to reconcile
 * it away), and "still connected" is the cheap, library-agnostic way to ask
 * whether this module is still looking at the served DOM.
 */
export function sweepBakedManagedHeadTags(
  snapshot: readonly Element[],
  input: { meta?: readonly HeadMetaTag[]; link?: readonly HeadLinkTag[] }
): number {
  if (snapshot.length === 0) return 0

  const declared = headInputIdentities(input)
  let removed = 0

  for (const el of snapshot) {
    if (!el || !el.isConnected) continue

    const tag = el.tagName.toUpperCase()
    const identity =
      tag === 'META'
        ? metaIdentity(el.getAttribute('name'), el.getAttribute('property'))
        : tag === 'LINK'
          ? linkIdentity(el.getAttribute('rel'), el.getAttribute('hreflang'))
          : null

    // Unidentifiable, or the runtime is about to own it (S2) — leave it.
    if (!identity || declared.has(identity)) continue

    el.remove()
    removed++
  }

  return removed
}
